import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, Legend, Cell,
} from "recharts";
import {
  Activity, TrendingUp, Target, Calendar as CalendarIcon,
  CheckCircle2, Layers, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";

const CURRENCY_COLORS = { USD: "#0D9488", EUR: "#2563EB", INR: "#D97706", AED: "#DC2626" };
const STAGE_LABEL = { new: "New", qualified: "Qualified", proposal: "Proposal", negotiation: "Negotiation", won: "Won", lost: "Lost" };
const STAGE_COLORS = { new: "#94A3B8", qualified: "#0EA5E9", proposal: "#8B5CF6", negotiation: "#F59E0B", won: "#16A34A", lost: "#DC2626" };

function fmtCurrency(cur, val) {
  const n = Number(val || 0);
  const opts = { maximumFractionDigits: n === Math.floor(n) ? 0 : 2 };
  return `${cur} ${n.toLocaleString(undefined, opts)}`;
}

function Card({ title, icon: Icon, testid, action, children }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-4 shadow-sm" data-testid={testid}>
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="h-4 w-4 text-brand-teal" /> : null}
          <h3 className="text-sm font-semibold">{title}</h3>
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

function ProgressRing({ value = 0, size = 96, stroke = 10, label }) {
  const clamped = Math.max(0, Math.min(100, Number(value) || 0));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (clamped / 100) * c;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none" className="stroke-muted" />
        <circle
          cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} fill="none"
          strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off}
          className="stroke-brand-teal transition-all duration-500"
        />
      </svg>
      <div className="absolute text-center">
        <div className="font-display text-xl font-semibold">{clamped}%</div>
        {label ? <div className="text-[10px] text-muted-foreground">{label}</div> : null}
      </div>
    </div>
  );
}

export default function CommandCenterAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState(""); // filter for pipeline/trend

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/command-center/analytics");
        if (alive) setData(data);
      } catch { /* silent */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const currencies = useMemo(() => data?.revenue_trend?.currencies || [], [data]);
  useEffect(() => { if (!currency && currencies.length === 1) setCurrency(currencies[0]); }, [currencies, currency]);

  if (loading) {
    return <div className="text-sm text-muted-foreground" data-testid="cc-analytics-loading">Loading executive dashboard…</div>;
  }
  if (!data) return null;

  const funnel = (data.pipeline_funnel || []).map((f) => ({
    stage: STAGE_LABEL[f.stage] || f.stage,
    key: f.stage,
    count: f.count,
    value: (f.value_by_currency || []).find((v) => !currency || v.currency === currency)?.value || 0,
    currencyBreakdown: f.value_by_currency || [],
  }));

  const trendSeries = data.revenue_trend?.series || [];
  const trendCurrencies = currency ? [currency] : currencies;

  const upcoming = data.upcoming_deadlines || [];
  const primary = data.primary_goal;
  const health = data.business_health || { score: 0, bucket: "watch", signals: {} };

  return (
    <div className="space-y-4" data-testid="cc-analytics">
      {/* Currency selector — only shown when multi-currency data exists */}
      {currencies.length > 1 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="cc-analytics-currency">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Currency</p>
          <div className="inline-flex overflow-hidden rounded-full border border-border">
            <button
              type="button"
              onClick={() => setCurrency("")}
              className={`px-3 py-1 text-[11px] font-semibold ${!currency ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
              data-testid="cc-analytics-currency-all"
            >
              All
            </button>
            {currencies.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c)}
                className={`px-3 py-1 text-[11px] font-semibold ${currency === c ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
                data-testid={`cc-analytics-currency-${c}`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {/* Business Health */}
        <Card title="Business Health" icon={Activity} testid="cc-analytics-health">
          <div className="flex items-center gap-4">
            <ProgressRing value={health.score} label={health.bucket.replace("_", " ")} />
            <ul className="flex-1 space-y-1 text-[11px]">
              <li className="flex justify-between"><span className="text-muted-foreground">Task completion</span><span className="font-semibold">{health.signals?.task_completion ?? 0}%</span></li>
              <li className="flex justify-between"><span className="text-muted-foreground">Goal progress</span><span className="font-semibold">{health.signals?.goal_progress ?? 0}%</span></li>
              <li className="flex justify-between"><span className="text-muted-foreground">Follow-up health</span><span className="font-semibold">{health.signals?.follow_up_health ?? 0}%</span></li>
              <li className="flex justify-between"><span className="text-muted-foreground">Overdue health</span><span className="font-semibold">{health.signals?.overdue_health ?? 0}%</span></li>
            </ul>
          </div>
        </Card>

        {/* Goal Progress Ring */}
        <Card title="Primary goal" icon={Target} testid="cc-analytics-primary-goal">
          {primary ? (
            <div className="flex items-center gap-4">
              <ProgressRing value={primary.progress} label="progress" />
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-semibold">{primary.name}</p>
                {primary.target ? <p className="text-[11px] text-muted-foreground">Target: {primary.target} {primary.target_unit || ""}</p> : null}
                {primary.target_date ? <p className="text-[11px] text-muted-foreground">by {primary.target_date.slice(0, 10)}</p> : null}
                <Link to="/app/goals" data-testid="cc-analytics-goals-link" className="mt-1 inline-block text-[11px] font-semibold text-brand-teal hover:underline">Open goals →</Link>
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">
              <p>No strategic goal set yet.</p>
              <Link to="/app/goals" className="mt-1 inline-block text-[11px] font-semibold text-brand-teal hover:underline">Add your first goal →</Link>
            </div>
          )}
        </Card>

        {/* Task Completion */}
        <Card title="Task completion" icon={CheckCircle2} testid="cc-analytics-tasks">
          <div className="flex items-center gap-4">
            <ProgressRing value={data.task_completion?.percent || 0} label="done" />
            <ul className="flex-1 space-y-1 text-[11px]">
              <li className="flex justify-between"><span className="text-muted-foreground">Total tasks</span><span className="font-semibold">{data.task_completion?.total || 0}</span></li>
              <li className="flex justify-between"><span className="text-muted-foreground">Completed</span><span className="font-semibold text-brand-green">{data.task_completion?.done || 0}</span></li>
              <li className="flex justify-between"><span className="text-muted-foreground">Won deals</span><span className="font-semibold">{data.won_count || 0}</span></li>
              <li className="flex justify-between"><span className="text-muted-foreground">Lost deals</span><span className="font-semibold text-brand-red">{data.lost_count || 0}</span></li>
            </ul>
          </div>
        </Card>
      </div>

      <div className="grid gap-4 xl:grid-cols-3">
        {/* Pipeline Funnel */}
        <Card title="Pipeline funnel" icon={Layers} testid="cc-analytics-funnel">
          {funnel.every((f) => f.count === 0) ? (
            <p className="text-sm text-muted-foreground">No deals yet. Add or import CRM data to see your pipeline shape.</p>
          ) : (
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={funnel} layout="vertical" margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                  <XAxis type="number" hide />
                  <YAxis dataKey="stage" type="category" width={78} tick={{ fontSize: 11 }} />
                  <Tooltip
                    formatter={(v) => [`${v} deals`, "Count"]}
                    contentStyle={{ borderRadius: 12, fontSize: 12 }}
                  />
                  <Bar dataKey="count" radius={[0, 8, 8, 0]}>
                    {funnel.map((f) => (
                      <Cell key={f.key} fill={STAGE_COLORS[f.key] || "#0D9488"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          <p className="mt-2 text-[10px] text-muted-foreground">Per-stage counts. Values by currency shown below.</p>
        </Card>

        {/* Sales by Stage (values) */}
        <Card title="Sales value by stage" icon={TrendingUp} testid="cc-analytics-by-stage">
          {funnel.every((f) => (f.currencyBreakdown || []).length === 0) ? (
            <p className="text-sm text-muted-foreground">No pipeline value recorded.</p>
          ) : (
            <ul className="space-y-2" data-testid="cc-analytics-by-stage-list">
              {funnel.map((f) => (
                <li key={f.key} className="rounded-xl border border-border/60 p-2 text-[11px]">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold" style={{ color: STAGE_COLORS[f.key] }}>{f.stage}</span>
                    <span className="text-muted-foreground">{f.count} deal{f.count === 1 ? "" : "s"}</span>
                  </div>
                  {(f.currencyBreakdown || []).length > 0 && (
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                      {(f.currencyBreakdown || []).filter((v) => !currency || v.currency === currency).map((v) => (
                        <span key={v.currency}>{fmtCurrency(v.currency, v.value)}</span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Upcoming Deadlines */}
        <Card title="Upcoming deadlines (30d)" icon={CalendarIcon} testid="cc-analytics-upcoming">
          {upcoming.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nothing on the horizon.</p>
          ) : (
            <ul className="space-y-1.5 text-[12px]" data-testid="cc-analytics-upcoming-list">
              {upcoming.slice(0, 8).map((u) => (
                <li key={`${u.kind}-${u.id}`} className="flex items-baseline justify-between gap-2">
                  <Link to={u.route || "/app"} className="min-w-0 flex-1 truncate hover:text-brand-teal">
                    <span className="mr-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{u.kind.replace("_", " ")}</span>
                    {u.label}
                  </Link>
                  <span className="shrink-0 text-[10px] text-muted-foreground">{(u.at || "").slice(0, 10)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {/* Revenue Trend — full-width, only shown when won revenue exists */}
      {trendSeries.some((row) => trendCurrencies.some((c) => Number(row[c] || 0) > 0)) && (
        <Card title="Revenue trend (last 12 months, per currency)" icon={TrendingUp} testid="cc-analytics-trend">
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <LineChart data={trendSeries} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.4} />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {trendCurrencies.map((c) => (
                  <Line key={c} type="monotone" dataKey={c} stroke={CURRENCY_COLORS[c] || "#0D9488"} strokeWidth={2} dot={{ r: 2 }} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">Currencies are shown as separate series and never combined.</p>
        </Card>
      )}
    </div>
  );
}
